import axiosInstance from './axiosInstance';

const isHeic = (file) => {
  if (file.type === 'image/heic' || file.type === 'image/heif') return true;
  const ext = file.name.split('.').pop().toLowerCase();
  return ext === 'heic' || ext === 'heif';
};

const convertHeicToJpeg = async (file) => {
  const heic2any = (await import('heic2any')).default;
  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const converted = Array.isArray(blob) ? blob[0] : blob;
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([converted], newName, { type: 'image/jpeg' });
};

/**
 * S3 파일 업로드 (3단계 플로우를 단일 함수로 캡슐화)
 *
 * 1. Presigned URL 발급
 * 2. S3에 직접 PUT 업로드 (Authorization 헤더 제외)
 * 3. 업로드 확정 → { fileEntityId, url } 반환
 *
 * @param {File} file - 브라우저 File 객체
 * @param {"IMAGE"|"VIDEO"|"DOCUMENT"} fileType
 * @returns {Promise<{ fileEntityId: number, url: string, thumbUrl: string|null, mediumUrl: string|null }>}
 */
const MAX_SIZES = { IMAGE: 20 * 1024 * 1024, VIDEO: 500 * 1024 * 1024, DOCUMENT: 50 * 1024 * 1024 };
/**
 * 서버가 받아주는 확장자와 맞춘 목록 (POST /api/files/presigned-url이 확장자를 검사한다).
 * 여기서 먼저 걸러야 사용자가 S3 왕복을 거친 뒤에야 거절당하지 않는다.
 * svg는 스크립트가 심길 수 있어 서버가 막으므로 여기에도 넣지 않는다.
 */
const ALLOWED_MIMES = {
  IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'],
  VIDEO: ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/x-msvideo', 'video/webm'],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/haansofthwp',
    'application/x-hwp',
    'application/hwp+zip',
    'text/plain',
    // 브라우저가 종류를 모르는 파일(.hwp, .hwpx 등)은 빈 문자열로 온다
    '',
  ],
};

export const uploadFile = async (file, fileType) => {
  // 파일 크기 검증
  const maxSize = MAX_SIZES[fileType];
  if (maxSize && file.size > maxSize) {
    const mb = Math.round(maxSize / 1024 / 1024);
    throw new Error(`파일 크기가 ${mb}MB를 초과합니다. (현재: ${Math.round(file.size / 1024 / 1024)}MB)`);
  }

  // MIME 타입 검증 (DOCUMENT는 빈 타입 허용 — .hwp 등)
  const allowed = ALLOWED_MIMES[fileType];
  if (allowed && file.type && !allowed.includes(file.type) && !isHeic(file)) {
    throw new Error(`지원하지 않는 파일 형식입니다. (${file.type})`);
  }

  // HEIC/HEIF → JPEG 변환 (아이폰 기본 포맷 대응)
  if (fileType === 'IMAGE' && isHeic(file)) {
    file = await convertHeicToJpeg(file);
  }

  // 브라우저가 MIME 타입을 인식하지 못하는 파일(.hwp, .zip 등)은 file.type이 "" → fallback
  const mimeType = file.type || 'application/octet-stream';

  // Step 1 — Presigned URL 발급
  const { data: { presignedUrl, storedName } } = await axiosInstance.post(
    '/api/files/presigned-url',
    {
      originalName: file.name,
      fileType,
      mimeType,
    }
  );

  // Step 2 — S3에 직접 PUT
  // X-Amz-SignedHeaders=host 만 서명에 포함 → Content-Type 포함 시 서명 불일치 403
  // axios 기본 인터셉터·헤더(Content-Type, Authorization 등)를 완전히 제거하기 위해 fetch 사용
  const s3Res = await fetch(presignedUrl, { method: 'PUT', body: file });
  if (!s3Res.ok) {
    const errText = await s3Res.text().catch(() => '');
    throw new Error(`S3 업로드 실패 (${s3Res.status}): ${errText}`);
  }

  // Step 3 — 업로드 확정
  const { data: confirmData } = await axiosInstance.post('/api/files/confirm', {
    storedName,
    sizeBytes: file.size,
    fileType,
    originalName: file.name,
    mimeType,
  });

  const fileEntityId = confirmData.id;
  // 백엔드가 반환하는 CloudFront URL 사용 (필드명 후보 순서대로 시도)
  const url =
    confirmData.url ??
    confirmData.imageUrl ??
    confirmData.fileUrl ??
    confirmData.publicUrl ??
    null;

  if (!url) {
    throw new Error('파일 업로드 확정 응답에 URL이 없습니다. 백엔드 응답을 확인하세요.');
  }

  // 리사이즈는 Lambda 비동기 처리라 이 시점에는 아직 없을 수 있다.
  // 미리보기는 원본으로 띄우고, FadeImage가 이후 리사이즈본으로 물러난다.
  return {
    fileEntityId,
    url,
    thumbUrl: confirmData.thumbUrl ?? null,
    mediumUrl: confirmData.mediumUrl ?? null,
  };
};
